import { requireSupabase } from './supabase'

const baseURL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

export interface OrganizationMembership {
  id: string
  name: string
  slug: string
  role: 'owner' | 'admin' | 'member'
}

export interface CurrentUser {
  id: string
  email?: string
  superAdmin: boolean
  organizations: OrganizationMembership[]
}

export interface Organization {
  id: string
  name: string
  slug: string
  status: 'trial' | 'active' | 'suspended' | 'cancelled'
  timezone: string
  currency: string
}

export interface Entitlement {
  key: string
  name: string
  enabled: boolean
  limit?: number
  used: number
  remaining?: number
  metered: boolean
}

export interface SubscriptionOverview {
  id: string
  organizationId: string
  status: 'trialing' | 'active' | 'past_due' | 'suspended' | 'cancelled' | 'expired'
  plan: { id: string; code: string; name: string; trialDays: number }
  currentPeriodStart: string
  currentPeriodEnd: string
  cancelAtPeriodEnd: boolean
  pastDueAt?: string
  graceEndsAt?: string
  accessState: 'full' | 'grace' | 'blocked'
  entitlements: Entitlement[]
}

export interface PlanPrice {
  id: string
  billingInterval: 'month' | 'year'
  currency: string
  amountCents: number
}

export interface PlanCapability {
  key: string
  name: string
  enabled: boolean
  limit?: number
}

export interface PublicPlan {
  id: string
  code: string
  name: string
  description?: string
  trialDays: number
  prices: PlanPrice[]
  capabilities: PlanCapability[]
}

export interface BillingConfiguration {
  provider: 'mercado_pago'
  checkoutConfigured: boolean
  webhookConfigured: boolean
}

export interface CheckoutSession {
  id: string
  organizationId: string
  planPriceId: string
  provider: string
  checkoutUrl?: string
  status: string
}

export interface LegalDocument {
  id: string
  documentType: 'terms_of_use' | 'privacy_policy'
  version: string
  title: string
  contentUrl: string
  contentHash: string
  required: boolean
}

export interface OnboardingStatus {
  profileComplete: boolean
  consentsComplete: boolean
  organizationComplete: boolean
  completed: boolean
  organizationId?: string
  organizationName?: string
  organizationSlug?: string
  selectedPlanId?: string
  selectedPlanPriceId?: string
  completedAt?: string
}

export interface ApiProblem {
  type: string
  title: string
  status: number
  detail?: string
  requestId: string
}

export type ContactKind = 'person' | 'company'
export type ContactStatus = 'active' | 'archived'

export interface Contact {
  id: string
  organizationId: string
  kind: ContactKind
  name: string
  legalName?: string
  taxId?: string
  email?: string
  phone?: string
  postalCode?: string
  street?: string
  addressNumber?: string
  complement?: string
  neighborhood?: string
  city?: string
  state?: string
  country: string
  notes?: string
  status: ContactStatus
  createdAt: string
  updatedAt: string
}

export type ContactInput = Omit<Contact, 'id' | 'organizationId' | 'status' | 'createdAt' | 'updatedAt'>

export interface ContactPage {
  items: Contact[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

export type CatalogKind = 'material' | 'service'
export interface CatalogCategory { id: string; organizationId: string; name: string; status: ContactStatus; createdAt: string; updatedAt: string }
export interface CatalogItem {
  id: string; organizationId: string; categoryId?: string; categoryName?: string; supplierContactId?: string; supplierName?: string
  kind: CatalogKind; sku?: string; name: string; description?: string; unit: string; costCents: number; salePriceCents: number
  marginPercent: number; status: ContactStatus; createdAt: string; updatedAt: string
}
export interface CatalogItemInput { categoryId?: string; supplierContactId?: string; kind: CatalogKind; sku: string; name: string; description: string; unit: string; costCents: number; salePriceCents: number }
export interface CatalogPage { items: CatalogItem[]; page: number; pageSize: number; total: number; totalPages: number }
export interface CatalogPriceHistory { id: string; previousCostCents?: number; previousSalePriceCents?: number; costCents: number; salePriceCents: number; changedBy: string; createdAt: string }

export type QuoteStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'cancelled'
export interface ComponentLineInput { catalogItemId?: string; childComponentId?: string; quantity: number }
export interface ComponentInput { sku: string; name: string; description: string; unit: string; salePriceCents: number; lines: ComponentLineInput[] }
export interface ComponentLine extends ComponentLineInput { id: string; position: number; sourceType: 'catalog' | 'component'; sourceName: string; sourceUnit: string; unitCostCents: number; totalCostCents: number }
export interface Component { id: string; organizationId: string; sku?: string; name: string; description?: string; unit: string; costCents: number; salePriceCents: number; marginPercent: number; status: ContactStatus; lines?: ComponentLine[]; createdAt: string; updatedAt: string }
export interface ComponentPage { items: Component[]; page: number; pageSize: number; total: number; totalPages: number }

export interface QuoteItemInput { catalogItemId?: string; componentId?: string; kind: 'material' | 'service' | 'component' | 'custom'; description: string; unit: string; quantity: number; unitCostCents: number; unitPriceCents: number }
export interface QuoteInput { contactId: string; title: string; validUntil?: string; notes: string; discountCents: number; items: QuoteItemInput[] }
export interface QuoteItem extends QuoteItemInput { id: string; position: number; costTotalCents: number; totalCents: number }
export interface Quote { id: string; organizationId: string; contactId: string; contactName: string; number: number; title: string; status: QuoteStatus; validUntil?: string; notes?: string; subtotalCents: number; discountCents: number; totalCents: number; costTotalCents: number; marginCents: number; marginPercent: number; items?: QuoteItem[]; createdAt: string; updatedAt: string }
export interface QuotePage { items: Quote[]; page: number; pageSize: number; total: number; totalPages: number }

export class ApiError extends Error {
  constructor(public readonly problem: ApiProblem) {
    super(problem.detail || problem.title)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit, organizationId?: string): Promise<T> {
  const supabase = requireSupabase()
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token

  const response = await fetch(`${baseURL}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(organizationId ? { 'X-Organization-ID': organizationId } : {}),
      ...init?.headers,
    },
  })

  if (!response.ok) {
    const fallback: ApiProblem = {
      type: 'about:blank',
      title: 'Erro na comunicação com o servidor',
      status: response.status,
      requestId: response.headers.get('X-Request-Id') ?? 'unknown',
    }
    const problem = await response.json().catch(() => fallback) as ApiProblem
    throw new ApiError(problem)
  }

  if (response.status === 204) return undefined as T

  return response.json() as Promise<T>
}

async function publicRequest<T>(path: string): Promise<T> {
  const response = await fetch(`${baseURL}${path}`, { headers: { Accept: 'application/json' } })
  if (!response.ok) {
    const fallback: ApiProblem = {
      type: 'about:blank',
      title: 'Erro na comunicação com o servidor',
      status: response.status,
      requestId: response.headers.get('X-Request-Id') ?? 'unknown',
    }
    const problem = await response.json().catch(() => fallback) as ApiProblem
    throw new ApiError(problem)
  }
  return response.json() as Promise<T>
}

export const api = {
  publicPlans: () => publicRequest<{ items: PublicPlan[] }>('/api/v1/plans'),
  billingConfiguration: () => publicRequest<BillingConfiguration>('/api/v1/billing/configuration'),
  currentUser: () => request<CurrentUser>('/api/v1/me'),
  createOrganization: (input: { name: string; slug: string }) => request<Organization>('/api/v1/organizations', {
    method: 'POST',
    body: JSON.stringify(input),
  }),
  organization: (organizationId: string) => request<Organization>(`/api/v1/organizations/${organizationId}/`, undefined, organizationId),
  subscription: (organizationId: string) => request<SubscriptionOverview>(`/api/v1/organizations/${organizationId}/subscription`, undefined, organizationId),
  createBillingCheckout: (organizationId: string, priceId: string, idempotencyKey: string) => request<CheckoutSession>(`/api/v1/organizations/${organizationId}/billing/checkout`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify({ priceId }),
  }, organizationId),
  legalDocuments: () => request<{ items: LegalDocument[] }>('/api/v1/legal-documents'),
  onboardingStatus: () => request<OnboardingStatus>('/api/v1/onboarding'),
  saveOnboardingProfile: (input: { fullName: string; phone: string }) => request<void>('/api/v1/onboarding/profile', { method: 'PUT', body: JSON.stringify(input) }),
  acceptConsents: (documentIds: string[]) => request<void>('/api/v1/onboarding/consents', { method: 'POST', body: JSON.stringify({ documentIds }) }),
  completeOnboarding: (organizationId: string, priceId?: string) => request<{ completed: boolean }>('/api/v1/onboarding/complete', { method: 'POST', body: JSON.stringify({ organizationId, ...(priceId ? { priceId } : {}) }) }),
  contacts: (organizationId: string, filters: { q?: string; kind?: string; status?: string; page?: number; pageSize?: number }) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') params.set(key, String(value))
    })
    return request<ContactPage>(`/api/v1/organizations/${organizationId}/contacts?${params}`, undefined, organizationId)
  },
  createContact: (organizationId: string, input: ContactInput) => request<Contact>(`/api/v1/organizations/${organizationId}/contacts`, { method: 'POST', body: JSON.stringify(input) }, organizationId),
  updateContact: (organizationId: string, contactId: string, input: ContactInput) => request<Contact>(`/api/v1/organizations/${organizationId}/contacts/${contactId}`, { method: 'PUT', body: JSON.stringify(input) }, organizationId),
  archiveContact: (organizationId: string, contactId: string) => request<void>(`/api/v1/organizations/${organizationId}/contacts/${contactId}`, { method: 'DELETE' }, organizationId),
  restoreContact: (organizationId: string, contactId: string) => request<void>(`/api/v1/organizations/${organizationId}/contacts/${contactId}/restore`, { method: 'POST' }, organizationId),
  catalogCategories: (organizationId: string) => request<{ items: CatalogCategory[] }>(`/api/v1/organizations/${organizationId}/catalog/categories`, undefined, organizationId),
  createCatalogCategory: (organizationId: string, name: string) => request<CatalogCategory>(`/api/v1/organizations/${organizationId}/catalog/categories`, { method: 'POST', body: JSON.stringify({ name }) }, organizationId),
  catalogItems: (organizationId: string, filters: { q?: string; kind?: string; status?: string; categoryId?: string; page?: number; pageSize?: number }) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => { if (value !== undefined && value !== '') params.set(key, String(value)) })
    return request<CatalogPage>(`/api/v1/organizations/${organizationId}/catalog/items?${params}`, undefined, organizationId)
  },
  createCatalogItem: (organizationId: string, input: CatalogItemInput) => request<CatalogItem>(`/api/v1/organizations/${organizationId}/catalog/items`, { method: 'POST', body: JSON.stringify(input) }, organizationId),
  updateCatalogItem: (organizationId: string, itemId: string, input: CatalogItemInput) => request<CatalogItem>(`/api/v1/organizations/${organizationId}/catalog/items/${itemId}`, { method: 'PUT', body: JSON.stringify(input) }, organizationId),
  archiveCatalogItem: (organizationId: string, itemId: string) => request<void>(`/api/v1/organizations/${organizationId}/catalog/items/${itemId}`, { method: 'DELETE' }, organizationId),
  restoreCatalogItem: (organizationId: string, itemId: string) => request<void>(`/api/v1/organizations/${organizationId}/catalog/items/${itemId}/restore`, { method: 'POST' }, organizationId),
  catalogPriceHistory: (organizationId: string, itemId: string) => request<{ items: CatalogPriceHistory[] }>(`/api/v1/organizations/${organizationId}/catalog/items/${itemId}/price-history`, undefined, organizationId),
  components: (organizationId: string, filters: { q?: string; status?: string; page?: number; pageSize?: number }) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => { if (value !== undefined && value !== '') params.set(key, String(value)) })
    return request<ComponentPage>(`/api/v1/organizations/${organizationId}/components?${params}`, undefined, organizationId)
  },
  component: (organizationId: string, componentId: string) => request<Component>(`/api/v1/organizations/${organizationId}/components/${componentId}`, undefined, organizationId),
  createComponent: (organizationId: string, input: ComponentInput) => request<Component>(`/api/v1/organizations/${organizationId}/components`, { method: 'POST', body: JSON.stringify(input) }, organizationId),
  updateComponent: (organizationId: string, componentId: string, input: ComponentInput) => request<Component>(`/api/v1/organizations/${organizationId}/components/${componentId}`, { method: 'PUT', body: JSON.stringify(input) }, organizationId),
  archiveComponent: (organizationId: string, componentId: string) => request<void>(`/api/v1/organizations/${organizationId}/components/${componentId}`, { method: 'DELETE' }, organizationId),
  restoreComponent: (organizationId: string, componentId: string) => request<void>(`/api/v1/organizations/${organizationId}/components/${componentId}/restore`, { method: 'POST' }, organizationId),
  quotes: (organizationId: string, filters: { q?: string; status?: string; contactId?: string; page?: number; pageSize?: number }) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => { if (value !== undefined && value !== '') params.set(key, String(value)) })
    return request<QuotePage>(`/api/v1/organizations/${organizationId}/quotes?${params}`, undefined, organizationId)
  },
  quote: (organizationId: string, quoteId: string) => request<Quote>(`/api/v1/organizations/${organizationId}/quotes/${quoteId}`, undefined, organizationId),
  createQuote: (organizationId: string, input: QuoteInput) => request<Quote>(`/api/v1/organizations/${organizationId}/quotes`, { method: 'POST', body: JSON.stringify(input) }, organizationId),
  updateQuote: (organizationId: string, quoteId: string, input: QuoteInput) => request<Quote>(`/api/v1/organizations/${organizationId}/quotes/${quoteId}`, { method: 'PUT', body: JSON.stringify(input) }, organizationId),
  updateQuoteStatus: (organizationId: string, quoteId: string, status: QuoteStatus) => request<void>(`/api/v1/organizations/${organizationId}/quotes/${quoteId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }, organizationId),
}
